<template>
  <section class="clients section">
    <div class="container">
      <h1 class="heading-1">Vendors</h1>
      <div>
        <div
          class="d-flex justify-content-between align-items-center flex-wrap"
        >
          <div class="text-dark mb-4">
            <span>
              <b v-if="!loading">
                <i class="fas fa-spin fa-circle-notch" v-if="busy"></i>
                {{ busy ? "" : vendors.length }}/{{ total }}</b
              >
              <i class="fas fa-spin fa-circle-notch" v-if="loading"></i>
              Vendor{{ loading || total != 1 ? "s" : "" }}
            </span>
          </div>
          <div class="mb-4 d-flex align-items-start">
            <router-link to="/admin/vendors/create" class="btn btn-primary">
              <i class="fas fa-plus fa-fw"></i> Add New Vendor
            </router-link>
            <router-link to="/admin/vendors/import" class="btn btn-info ml-3">
              <i class="fas fa-file-upload fa-fw"></i> Import Vendors
            </router-link>
          </div>
        </div>
        <div class="position-relative mb-3" v-if="!loading">
          <input
            type="search"
            class="form-control"
            @keydown.enter="SearchVendorText"
            v-model.trim="searchContent"
            placeholder="Search by Vendor Name"
          />
          <span>
            <i
              class="fa fa-search search-icon text-primary"
              @click="SearchVendorText"
            ></i>
            <i
              v-if="searchContent"
              class="fas fa-times cancel-icon"
              @click="(searchContent = ''), SearchVendorText()"
            ></i>
          </span>
        </div>
      </div>
      <alert v-if="!loading && vendors.length == 0" class="white-bg shadow-card"
        >No Vendors</alert
      >
      <loading v-if="loading" class="white-bg shadow-card" />

      <div
        class="overflow-auto desktop-shadow-card rounded"
        v-if="vendors.length > 0 && !loading"
      >
        <table
          class="table table-striped mb-0 responsive-mobile-tablet-table desktop-shadow-card rounded"
        >
          <thead>
            <tr>
              <th>Vendor</th>
              <th>Created</th>
              <th class="text-center">Active</th>
              <th class="text-right pr-5">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(v, index) in vendors"
              :key="v.id"
              class="mobile-tablet-shadow-card rounded"
              :id="`vendor_${index}`"
            >
              <td>
                <h2 class="m-0">
                  <b class="mobile-tablet-only mr-2">Name:</b>
                  <icon type="building" lib="far" class="mr-2 text-muted" />
                  {{ v.name }}
                </h2>
                <b class="mobile-tablet-only mr-2">Location(s):</b>
                <span v-for="(loc, idx) in v.locations" :key="loc.id">
                  <badge
                    class="mr-2"
                    v-if="
                      idx < 20 &&
                      ((loc.street || '').trim() != '' ||
                        (loc.city || '').trim() != '' ||
                        (loc.state || '').trim() != '')
                    "
                  >
                    <span v-if="(loc.street || '').trim()">{{
                      loc.street
                    }}</span>
                    <span v-else>{{ loc.city }}, {{ loc.state }}</span>
                    <span v-if="loc.clinic_name"> ({{ loc.clinic_name }})</span>
                  </badge>
                </span>
                <span v-if="v.locations.length >= 20">
                  <badge>
                    <span class="font-weight-bold"
                      >...{{ v.locations.length - 20 }} more locations</span
                    >
                  </badge>
                </span>
              </td>
              <td>
                <b class="mobile-tablet-only mr-2">Created:</b>
                <span v-if="v.created_at">{{ v.created_at | fullDate }}</span>
                <span v-else>-</span>
              </td>
              <td class="text-center">
                <b class="mobile-tablet-only mr-2">Status:</b>
                <i
                  class="fas fa-fw"
                  v-if="false"
                  :class="
                    v.active
                      ? `fa-check text-success`
                      : `fa-times text-secondary`
                  "
                ></i>
                <button
                  type="button"
                  v-if="canEdit(v)"
                  @click="updateVendorStatus(v)"
                  :disabled="v.activating"
                  class="btn btn-sm mr-2 mb-2"
                  :class="v.active ? 'btn-success' : 'btn-warning'"
                >
                  <span class="mobile-tablet-only mr-2">
                    {{ v.active ? "Deactivate" : "Activate" }}
                  </span>
                  <i
                    v-if="!v.activating"
                    class="fas"
                    :class="v.active ? 'fa-user-alt' : 'fa-user-alt-slash'"
                    title="Deactivate/activate"
                  ></i>
                  <span
                    class="spinner-border spinner-border-sm"
                    v-if="v.activating"
                    role="status"
                    aria-hidden="true"
                  ></span>
                </button>
                <div
                  type="button"
                  v-if="!canEdit(v)"
                  class="mr-2 mb-2 alert px-2 py-1 small d-inline-block"
                  :class="v.active ? 'alert-success' : 'alert-warning'"
                >
                  {{ v.active ? "Active" : "Inactive" }}
                </div>
              </td>
              <td class="text-right td-actions">
                <button
                  type="button"
                  :data-v-step="`editVendorProfile_${index}`"
                  @click="editVendor(v)"
                  class="btn btn-info btn-sm mr-2 mb-2"
                >
                  <b class="mobile-tablet-only mr-2">{{
                    canEdit(v) ? "Edit" : "View"
                  }}</b>
                  <i
                    :class="`fas fa-${canEdit(v) ? 'edit' : 'search'} fa-fw`"
                    :title="canEdit(v) ? 'edit' : 'view'"
                  ></i>
                </button>
                <button
                  type="button"
                  v-if="canEdit(v)"
                  class="btn btn-danger btn-sm mr-2 mb-2"
                  @click="deleteVendor(v)"
                >
                  <b class="mobile-tablet-only mr-2">Delete</b>
                  <i class="fas fa-trash fa-fw" title="delete"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <mugen-scroll :handler="loadMore" :should-handle="!busy && !loading">
          <alert
            v-if="busy"
            class="text-center p-2 mb-0 mobile-tablet-loadmore mobile-tablet-shadow-card"
            ><i class="fas fa-spin fa-circle-notch mr-2"></i>Loading more
            vendors...</alert
          >
        </mugen-scroll>
      </div>
    </div>
  </section>
</template>
<script>
import { mapState, mapActions } from "vuex";
import Swal from "sweetalert2";
import MugenScroll from "vue-mugen-scroll";
import Auth from "../../auth";

export default {
  name: "vendors",
  components: {
    MugenScroll,
  },
  data() {
    return {
      vendor: false,
      limit: 50,
      busy: false,
      latestSearch: "",
      searchContent: "",
    };
  },
  computed: {
    ...mapState(["loading"]),
    ...mapState({
      vendors: (state) => state.vendors.pagination.data,
      current: (state) => state.vendors.pagination.current,
      total: (state) => state.vendors.pagination.total || 0,
    }),
    user: function () {
      return Auth.user.get();
    },
  },
  mounted() {
    if (!this.user.isAdmin) {
      this.getVendors({
        limit: this.limit,
        contactsOnly: true,
        sortByDate: true,
      });
    } else {
      this.getVendors({ limit: this.limit, sortByDate: true });
    }
  },
  methods: {
    ...mapActions({
      getVendors: "vendors/allWithoutLocs",
    }),
    editVendor: function (vendor) {
      this.$router.push({
        path: `/vendors/${vendor.id}`,
      });
    },
    canEdit: function (vendor) {
      if (this.user.isAdmin) {
        return true;
      }
      if (this.user.isVendor && (vendor.admins || []).includes(this.user.id)) {
        return true;
      }
      return false;
    },
    deleteVendor: function (vendor) {
      const swalWithBootstrapButtons = Swal.mixin({
        customClass: {
          confirmButton: "btn btn-success ml-4",
          cancelButton: "btn btn-danger",
        },
        buttonsStyling: false,
      });

      swalWithBootstrapButtons
        .fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          type: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, delete",
          cancelButtonText: "No, cancel",
          reverseButtons: true,
        })
        .then((result) => {
          if (result.value) {
            this.$store.dispatch("vendors/delete", { id: vendor.id });
          }
        });
    },
    updateVendorStatus: function (vendor) {
      const swalWithBootstrapButtons = Swal.mixin({
        customClass: {
          confirmButton: "btn btn-success ml-4",
          cancelButton: "btn btn-danger",
        },
        buttonsStyling: false,
      });
      swalWithBootstrapButtons
        .fire({
          title: "Are you sure?",
          text: `You are going to ${
            vendor.active ? "de" : ""
          }activate this vendor`,
          type: "warning",
          showCancelButton: true,
          confirmButtonText: `Yes, ${vendor.active ? "de" : ""}activate`,
          cancelButtonText: "No, cancel!",
          reverseButtons: true,
        })
        .then((result) => {
          if (result.value) {
            vendor.activating = true;
            this.$forceUpdate();
            this.$store
              .dispatch("vendors/toggleActivation", {
                vendor,
                active: vendor.active,
              })
              .then(() => {
                vendor.activating = false;
                this.$forceUpdate();
              });
          }
        });
    },
    getFilteredVendors: function (page) {
      const data = {
        limit: this.limit,
      };
      if (page) {
        data.page = page;
      }
      if (!this.user.isAdmin) {
        data.contactsOnly = true;
      }
      if (this.searchContent) {
        data.vendorName = this.searchContent;
      }
      this.getVendors(data).then(() => {
        this.busy = false;
      });
    },
    SearchVendorText: function () {
      if (this.latestSearch == this.searchContent) {
        return;
      }
      this.latestSearch = this.searchContent;
      this.getFilteredVendors();
    },
    loadMore() {
      if (this.vendors.length < this.total) {
        this.busy = true;
        this.getFilteredVendors(this.current + 1);
      }
    },
  },
};
</script>
<style scoped lang="scss">
.form-group {
  position: relative;
}
.cancel-icon,
.search-icon {
  z-index: 99;
  position: absolute;
  right: 40px;
  top: 50%;
  -webkit-transform: translateY(-50%);
  transform: translateY(-50%);
  color: #dc143c;
  cursor: pointer;
}
.search-icon {
  right: 15px;
}
</style>
